/* TanStack Query, and the IndexedDB persistence that makes this app useful when
   the tenant is unreachable — the argument the whole PWA decision rests on (§2).

   The service worker has the app shell; this has the data. Keeping them separate
   is deliberate: an opaque cache full of stale list responses and auth traffic is
   not something you can reason about at 3am. */

import { QueryClient } from '@tanstack/react-query';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get, set, del } from 'idb-keyval';

/** 30 days. DR data has to survive a long quiet period and still be there. */
const MAX_AGE = 1000 * 60 * 60 * 24 * 30;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /* Long, because the underlying data changes on a nightly sync, not by the
         minute, and refetching on every focus is wasted battery on a phone. */
      staleTime: 1000 * 60 * 5,
      gcTime: MAX_AGE,
      retry: (failureCount, error) => {
        /* Never retry an auth failure — it will not resolve itself, and three
           attempts just delay the sign-in prompt. */
        const status = (error as { status?: number } | null)?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      /* Serve what we have, immediately, then revalidate. Offline this is the
         only behaviour that renders anything at all. */
      networkMode: 'offlineFirst',
    },
    mutations: {
      /* Reads offline, writes online (§7 rule 1). No sync queue, no conflict
         resolution — a genuinely hard problem this app does not have. A mutation
         attempted offline fails fast and the UI says so. */
      networkMode: 'online',
      retry: 0,
    },
  },
});

const idbStorage = {
  getItem: (key: string) => get<string>(key).then((v) => v ?? null),
  setItem: (key: string, value: string) => set(key, value),
  removeItem: (key: string) => del(key),
};

export const persister = createAsyncStoragePersister({
  storage: idbStorage,
  key: 'hird-blueprint-cache',
  throttleTime: 1000,
});

export const persistOptions = {
  persister,
  maxAge: MAX_AGE,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { queryKey: readonly unknown[]; state: { status: string } }) => {
      return query.state.status === 'success';
    },
  },
};

/** Bumped when the cached shape changes, so an old cache is dropped rather than
    deserialised into types that no longer match. */
export const CACHE_BUSTER = 'v1';
